import { useMemo } from 'react';
import ProjectMember from '@projectMembers/models/projectMember';
import Text from '@ui/components/Text/Text';
import { Anchor, Avatar, Box, Group, HoverCard } from '@mantine/core';
import ProjectRole, { ProjectMemberRoleType } from '@projectMembers/models/projectRole';
import Icon from '@ui/components/Icon/Icon';
import Routes from 'src/new_arch/navigation/routes';
import ProjectMemberRoleSelector, { RoleOption } from '../ProjectMemberRoleSelector/ProjectMemberRoleSelector';
import styles from './ProjectMemberListItem.module.scss';

type ProjectMemberListItemProps = {
	member: ProjectMember;
	isCurrentUser: boolean;
	currentUserRole: ProjectRole;
	roleOptions: RoleOption[];
	workspaceID: number;
	onDelete?: ( member: ProjectMember ) => void;
	onUpdateRole?: ( member: ProjectMember, role: ProjectMemberRoleType ) => void;
	onRequestSeat?: () => void;
	readonly?: boolean;
	showDelete?: boolean;
};

const warningChipStyles = {
	display: 'flex',
	alignItems: 'center'
};

type ViewerRelation = 'admin' | 'self' | 'other';

const computeViewerRelation = ( {
	isCurrentUserOwnerOrManager, isCurrentUser
}: { isCurrentUserOwnerOrManager: boolean; isCurrentUser: boolean } ): ViewerRelation => {
	if ( isCurrentUserOwnerOrManager ) return 'admin';
	if ( isCurrentUser ) return 'self';
	return 'other';
};

type WarningTooltipProps = {
	viewerRelation: ViewerRelation;
	workspaceID: number;
	onRequestSeat?: () => void;
};

const BASE_WARNING_TEXT = 'Can only comment, markup and approve assets.';
const ADMIN_RESTORE_HINT = 'Full Editor access can be restored by a workspace admin.';

const renderWarningContent = ( { viewerRelation, workspaceID, onRequestSeat }: WarningTooltipProps ) => {
	if ( viewerRelation === 'admin' ) {
		return (
			<>
				{BASE_WARNING_TEXT} Full Editor access can be restored,{' '}
				<Anchor
					href={Routes.toWorkspaceMembers( workspaceID )}
					target="_blank"
					style={{ color: 'white', textDecoration: 'underline' }}
				>
					manage member settings.
				</Anchor>
			</>
		);
	}
	if ( viewerRelation === 'self' ) {
		return (
			<>
				{BASE_WARNING_TEXT} {ADMIN_RESTORE_HINT}{' '}
				<span
					style={{ textDecoration: 'underline', cursor: 'pointer' }}
					onClick={onRequestSeat}
				>
					Request a seat
				</span>
			</>
		);
	}
	return <>{BASE_WARNING_TEXT} {ADMIN_RESTORE_HINT}</>;
};

const WarningTooltip = ( props: WarningTooltipProps ) => (
	<HoverCard position="bottom" offset={6} withArrow withinPortal closeDelay={150}>
		<HoverCard.Target>
			<Box style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
				<Icon icon="error_outline" size={14} color="var(--color-feedback-warning)" />
			</Box>
		</HoverCard.Target>
		<HoverCard.Dropdown style={{ maxWidth: 220, padding: '7px 8px', backgroundColor: '#061324', border: 'none', borderRadius: 4 }}>
			<Text variant="caption-regular" color="white" style={{ textAlign: 'center' }}>
				{renderWarningContent( props )}
			</Text>
		</HoverCard.Dropdown>
	</HoverCard>
);

const StatusChip = ( { label }: { label: string } ) => (
	<Text variant="caption-regular" color="var(--color-text-secondary)" style={{ whiteSpace: 'nowrap' }}>{label}</Text>
);

const GuestLinkIcon = () => (
	<Icon icon="link" size={14} color="var(--color-text-secondary)" />
);

const ProjectMemberListItem = ( {
	member,
	isCurrentUser,
	currentUserRole,
	roleOptions,
	workspaceID,
	onDelete,
	onUpdateRole,
	onRequestSeat,
	readonly = false,
	showDelete = false
}: ProjectMemberListItemProps ) => {
	const roleOptionsForMember = useMemo( () => (
		roleOptions
			.filter( option => !member.isGuest || option.role.canBeInvitedByLink() )
	), [ roleOptions, member.isGuest ] );

	const isOwner = member.role.id === ProjectRole.owner().id;
	const showWarningChip = member.isLimitedEditor;
	const isCurrentUserOwnerOrManager = currentUserRole.isOwner || currentUserRole.isManager;
	const viewerRelation: ViewerRelation = computeViewerRelation( {
		isCurrentUserOwnerOrManager, isCurrentUser
	} );

	return (
		<Group position="apart" className={styles.ProjectMemberListItem} noWrap>
			<Group spacing={5} noWrap style={{ minWidth: 0 }}>
				<Avatar size={20} radius="xl" src={member.user.avatar} />
				<Group spacing={2} noWrap style={{ minWidth: 0 }}>
					<Text variant="footnote" color="var(--color-text-primary)" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{member.user.email}
					</Text>
					{isCurrentUser && <StatusChip label="(You)" />}
					{member.isGuest && (
						<Group spacing={2} noWrap>
							<StatusChip label="(Guest)" />
							<GuestLinkIcon />
						</Group>
					)}
				</Group>
			</Group>
			<Group spacing={2} noWrap>
				{showWarningChip && (
					<Box style={warningChipStyles}>
						<WarningTooltip
							viewerRelation={viewerRelation}
							workspaceID={workspaceID}
							onRequestSeat={onRequestSeat}
						/>
					</Box>
				)}
				<ProjectMemberRoleSelector
					selectedRole={member.role}
					roleOptions={roleOptionsForMember}
					readonly={readonly || isCurrentUser || isOwner}
					showDelete={showDelete}
					onDelete={() => onDelete?.( member )}
					onChange={role => !member.hasRole( role ) && onUpdateRole?.( member, role )}
				/>
			</Group>
		</Group>
	);
};

export default ProjectMemberListItem;
